class SpoilerPreorderHubV4 {
  constructor(root) {
    this.root = root;
    this.apiUrl = root.dataset.apiUrl;

    this.search = root.querySelector('[data-hub-search]');
    this.driver = root.querySelector('[data-hub-driver]');
    this.scale = root.querySelector('[data-hub-scale]');
    this.month = root.querySelector('[data-hub-month]');
    this.sort = root.querySelector('[data-hub-sort]');

    this.count = root.querySelector('[data-hub-count]');
    this.updated = root.querySelector('[data-hub-updated]');
    this.loading = root.querySelector('[data-hub-loading]');
    this.results = root.querySelector('[data-hub-results]');
    this.empty = root.querySelector('[data-hub-empty]');
    this.error = root.querySelector('[data-hub-error]');

    this.products = [];
    this.searchTimer = null;
  }

  init() {
    if (!this.hasRequiredElements()) {
      console.error(
        'Preorder Hub could not initialize because required elements are missing.'
      );
      return;
    }

    this.bindEvents();
    this.load();
  }

  hasRequiredElements() {
    return Boolean(
      this.apiUrl &&
      this.search &&
      this.driver &&
      this.scale &&
      this.month &&
      this.sort &&
      this.count &&
      this.loading &&
      this.results &&
      this.empty &&
      this.error
    );
  }

  bindEvents() {
    this.search.addEventListener('input', () => {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.render(), 120);
    });

    [this.driver, this.scale, this.month, this.sort].forEach(control => {
      control.addEventListener('change', () => this.render());
    });

    this.root.addEventListener('click', event => {
      const button = event.target.closest('[data-direct-cart]');
      if (!button) return;

      event.preventDefault();

      if (button.disabled || button.dataset.soldOut === 'true') {
        return;
      }

      this.submitCartForm(button);
    });
  }

  async load() {
    try {
      const response = await fetch(this.apiUrl, {
        headers: {
          Accept: 'application/json'
        },
        credentials: 'omit'
      });

      if (!response.ok) {
        throw new Error(`Preorder Hub API returned ${response.status}`);
      }

      const payload = await response.json();

      this.products = Array.isArray(payload.products)
        ? payload.products
        : [];

      this.populateFilters();
      this.showUpdated(payload.generatedAt);

      this.loading.hidden = true;
      this.render();
    } catch (error) {
      console.error('Preorder Hub load failed:', error);
      this.loading.hidden = true;
      this.error.hidden = false;
    }
  }

  populateFilters() {
    const drivers = [
      ...new Set(
        this.products
          .map(product => product.driver)
          .filter(Boolean)
      )
    ].sort((a, b) => a.localeCompare(b));

    const months = [
      ...new Set(
        this.products
          .map(product => product.month)
          .filter(Boolean)
      )
    ].sort();

    drivers.forEach(value => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      this.driver.appendChild(option);
    });

    months.forEach(value => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = this.formatMonth(value);
      this.month.appendChild(option);
    });
  }

  showUpdated(value) {
    if (!this.updated || !value) return;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return;

    const formattedDate = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);

    this.updated.textContent = `Last updated ${formattedDate}`;
    this.updated.hidden = false;
  }

  render() {
    const query = this.normalize(this.search.value);
    const selectedDriver = this.normalize(this.driver.value);
    const selectedScale = this.scale.value;
    const selectedMonth = this.month.value;
    const descending = this.sort.value === 'eta-desc';

    const filtered = this.products
      .filter(product => {
        const haystack = this.normalize(
          [
            product.title,
            product.sku,
            product.driver
          ].join(' ')
        );

        const searchMatch =
          !query ||
          haystack.includes(query);

        const driverMatch =
          !selectedDriver ||
          this.normalize(product.driver) === selectedDriver;

        const scaleMatch =
          !selectedScale ||
          product.scale === selectedScale;

        const monthMatch =
          !selectedMonth ||
          product.month === selectedMonth;

        return (
          searchMatch &&
          driverMatch &&
          scaleMatch &&
          monthMatch
        );
      })
      .sort((a, b) => {
        const comparison = String(a.etaDate || '')
          .localeCompare(String(b.etaDate || ''));

        return descending ? -comparison : comparison;
      });

    this.count.textContent = String(filtered.length);
    this.empty.hidden = filtered.length !== 0;
    this.results.innerHTML = '';

    const groups = new Map();

    filtered.forEach(product => {
      const label = this.formatMonth(product.month);

      if (!groups.has(label)) {
        groups.set(label, []);
      }

      groups.get(label).push(product);
    });

    groups.forEach((products, label) => {
      const section = document.createElement('section');
      section.className = 'preorder-hub-v4__month';

      section.innerHTML = `
        <div class="preorder-hub-v4__month-heading">
          <h2>${this.escapeHtml(label)}</h2>
          <span>
            ${products.length}
            expected arrival${products.length === 1 ? '' : 's'}
          </span>
        </div>

        <div class="preorder-hub-v4__grid">
          ${products
            .map(product => this.productCard(product))
            .join('')}
        </div>
      `;

      this.results.appendChild(section);
    });
  }

  productCard(product) {
    const numericVariantId = this.variantNumericId(
      product.defaultVariantId
    );

    /*
      The API supplies an explicit sold-out flag.
      The purchasable count remains as a fallback so
      a sold-out product can never become a direct-add button.
    */
    const isSoldOut =
      product.soldOut === true ||
      product.availableForSale === false ||
      Number(product.purchasableVariantCount || 0) === 0;

    const canAddDirectly =
      !isSoldOut &&
      numericVariantId &&
      Number(product.purchasableVariantCount) === 1;

    const productUrl = this.escapeHtml(product.productUrl || '');
    const imageUrl = this.escapeHtml(product.imageUrl || '');
    const title = this.escapeHtml(product.title || '');

    const etaText = this.escapeHtml(
      String(product.etaText || '').replace(
        /^Shipping ETA\s+/i,
        ''
      )
    );

    const sku = this.escapeHtml(product.sku || '—');

    const scaleBadge = product.scale
      ? `
        <span
          class="
            preorder-hub-v4__badge
            preorder-hub-v4__badge--scale
          "
        >
          ${this.escapeHtml(product.scale)}
        </span>
      `
      : '';

    const driverBadge = product.driver
      ? `
        <span class="preorder-hub-v4__badge">
          ${this.escapeHtml(product.driver)}
        </span>
      `
      : '';

    let cartControl;

    if (isSoldOut) {
      cartControl = `
        <button
          type="button"
          class="
            preorder-hub-v4__cart
            preorder-hub-v4__cart--sold-out
          "
          data-sold-out="true"
          disabled
          aria-disabled="true"
        >
          Sold Out
        </button>
      `;
    } else if (canAddDirectly) {
      cartControl = `
        <button
          type="button"
          class="preorder-hub-v4__cart"
          data-direct-cart="${this.escapeHtml(numericVariantId)}"
        >
          Add To Cart
        </button>
      `;
    } else {
      /*
        Products with multiple purchasable variants still go
        to the product page so Shopify controls which variant
        the customer selects.
      */
      cartControl = `
        <a
          class="preorder-hub-v4__cart"
          href="${productUrl}"
          target="_blank"
          rel="noopener"
        >
          Add To Cart
        </a>
      `;
    }

    return `
      <article class="preorder-hub-v4__card">
        <a
          class="preorder-hub-v4__image"
          href="${productUrl}"
          target="_blank"
          rel="noopener"
        >
          <img
            src="${imageUrl}"
            alt="${title}"
            loading="lazy"
          >
        </a>

        <div class="preorder-hub-v4__body">
          <div class="preorder-hub-v4__badges">
            ${scaleBadge}
            ${driverBadge}
          </div>

          <a
            class="preorder-hub-v4__title"
            href="${productUrl}"
            target="_blank"
            rel="noopener"
          >
            ${title}
          </a>

          <div class="preorder-hub-v4__eta">
            <span>Shipping ETA</span>
            <strong>${etaText}</strong>
          </div>

          ${this.productionHtml(product.productionQuantities)}

          <div class="preorder-hub-v4__facts">
            <span>SKU ${sku}</span>
          </div>

          ${cartControl}
        </div>
      </article>
    `;
  }

  productionHtml(items) {
    if (!Array.isArray(items) || items.length === 0) {
      return '';
    }

    const rows = items
      .map(item => {
        const label = this.escapeHtml(item.label || '');
        const quantityValue = Number(item.quantity);

        const quantity = Number.isFinite(quantityValue)
          ? quantityValue.toLocaleString('en-US')
          : '';

        return `
          <div class="preorder-hub-v4__production-row">
            <strong>${label}</strong>
            <b>${quantity}</b>
          </div>
        `;
      })
      .join('');

    return `
      <div class="preorder-hub-v4__production">
        <span>Confirmed Production</span>
        ${rows}
      </div>
    `;
  }

  submitCartForm(button) {
    const variantId = button.dataset.directCart;

    if (!variantId || button.disabled) {
      return;
    }

    button.disabled = true;
    button.textContent = 'Adding…';

    const rootPath =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    const form = document.createElement('form');

    form.method = 'POST';
    form.action = `${rootPath}cart/add`;
    form.style.display = 'none';

    const fields = {
      form_type: 'product',
      utf8: '✓',
      id: variantId,
      quantity: '1',
      return_to: `${rootPath}cart`
    };

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');

      input.type = 'hidden';
      input.name = name;
      input.value = value;

      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  variantNumericId(value) {
    if (!value) return null;

    return String(value)
      .split('/')
      .pop();
  }

  formatMonth(value) {
    if (!value) return '';

    const date = new Date(`${value}-01T12:00:00Z`);

    if (Number.isNaN(date.getTime())) {
      return String(value);
    }

    return new Intl.DateTimeFormat(
      'en-US',
      {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC'
      }
    ).format(date);
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(
        /[&<>"']/g,
        character => {
          const replacements = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;'
          };

          return replacements[character];
        }
      );
  }
}

function initializePreorderHubV4(scope = document) {
  scope
    .querySelectorAll('[data-preorder-hub-v4]')
    .forEach(root => {
      if (
        root.dataset.preorderHubV4Initialized ===
        'true'
      ) {
        return;
      }

      root.dataset.preorderHubV4Initialized = 'true';

      const hub = new SpoilerPreorderHubV4(root);
      hub.init();
    });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initializePreorderHubV4();
    }
  );
} else {
  initializePreorderHubV4();
}

document.addEventListener(
  'shopify:section:load',
  event => {
    initializePreorderHubV4(event.target);
  }
);